import { push } from "connected-react-router";
import { omit } from "lodash-es";
import { all, call, fork, put, select, takeLatest } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import Route, { RoutePreview } from "@mapmycustomers/shared/types/entity/Route";
import PinLegend, {
  ColorPinLegend,
  ShapePinLegend,
} from "@mapmycustomers/shared/types/map/PinLegend";
import User from "@mapmycustomers/shared/types/User";
import { MapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import geoService from "@mapmycustomers/shared/util/geo/GeoService";

import { FILTER_ENTITY_TYPES } from "@app/component/filtering/utils/consts";
import Path from "@app/enum/Path";
import RouteSubPath from "@app/enum/RouteSubPath";
import computeRouteBounds from "@app/scene/routes/utils/computeRouteBounds";
import getRouteStopsTypes from "@app/scene/routes/utils/getRouteStopsTypes";
import { handleError } from "@app/store/errors/actions";
import { getCurrentUser, getUserMetaData } from "@app/store/iam";
import { getMapRecordsListSettings } from "@app/store/map/selectors";
import resolveLegendKeys from "@app/store/map/utils/resolveLegendKeys";
import { boundariesViewSettingsChanged } from "@app/store/mapLayers/boundaries/actions";
import { getBoundaryType } from "@app/store/mapLayers/boundaries/selectors";
import {
  getAllColorLegends,
  getAllShapeLegends,
  getDefaultColorKeys,
  getDefaultShapeKeys,
} from "@app/store/pinLegends";
import { selectSavedFilter } from "@app/store/savedFilters/actions";
import MapRecordsListState from "@app/types/viewModel/MapRecordsListState";
import createDefaultMapViewState from "@app/util/map/createDefaultMapViewState";

import {
  applyRouteRecordsListSettings,
  applyRouteViewSettings,
  fetchScheduledActivities,
  initializeRouteCreate,
  initializeRouteEdit,
  initializeRouteView,
} from "./actions";
import { editorSaga } from "./editor/sagas";
import { routeLeadFinderSagas } from "./leadFinder/sagas";
import { mapSaga } from "./map/sagas";
import { fetchRouteAndPreview, viewSaga } from "./view/sagas";

export function* buildInitialMapViewState(
  route: Route | undefined,
  options?: { includeRouteStopsTypes?: boolean; inheritSavedFilter?: boolean }
) {
  const includeRouteStopsTypes = options?.includeRouteStopsTypes ?? true;
  const inheritSavedFilter = options?.inheritSavedFilter ?? true;
  // read the user's saved route view settings
  const userMetaData: User["metaData"] = yield select(getUserMetaData);
  const persisted = userMetaData.routeViewSettings;

  // keep a persisted pin legend only if it still exists, else fall back to the org default
  const colorLegends: ColorPinLegend[] = yield select(getAllColorLegends);
  const shapeLegends: ShapePinLegend[] = yield select(getAllShapeLegends);
  const defaultColorKeys: Partial<Record<EntityTypesSupportedByMapsPage, PinLegend["id"]>> =
    yield select(getDefaultColorKeys);
  const defaultShapeKeys: Partial<Record<EntityTypesSupportedByMapsPage, PinLegend["id"]>> =
    yield select(getDefaultShapeKeys);
  const { colorKey, shapeKey } = resolveLegendKeys(
    persisted,
    colorLegends,
    shapeLegends,
    defaultColorKeys,
    defaultShapeKeys
  );

  // The id is only the label — the rules live in `filter` and their effect in `visibleEntities`.
  // Inheriting those without the id would leave a filter applied with no lit pill to clear it, so
  // a brand-new route drops all three rather than starting under the last route's filter.
  const inherited = inheritSavedFilter
    ? persisted
    : omit(persisted, ["filter", "selectedSavedFilterId", "visibleEntities"]);

  // the reducer merges `filter`, so `{}` leaves earlier rules standing — every key has to be
  // named to erase them
  const clearedFilter = FILTER_ENTITY_TYPES.reduce<MapFilterModel>((result, entityType) => {
    result[entityType] = undefined;
    return result;
  }, {});

  const baseViewState: MapViewState = {
    ...createDefaultMapViewState(),
    ...(inherited ?? {}),
    colorKey,
    filter: inheritSavedFilter ? (persisted?.filter ?? {}) : clearedFilter,
    shapeKey,
  };

  if (!route || !includeRouteStopsTypes) {
    return baseViewState;
  }

  // ensure the route's stop entity types are visible on the map
  const routeStopsTypes = getRouteStopsTypes(route);
  const seededViewState: MapViewState = {
    ...baseViewState,
    visibleEntities: Array.from(new Set([...baseViewState.visibleEntities, ...routeStopsTypes])),
  };
  return seededViewState;
}

function* initRecordListState() {
  const settings: MapRecordsListState | undefined = yield select(getMapRecordsListSettings);
  if (settings?.sort) {
    yield put(applyRouteRecordsListSettings({ sort: settings.sort }));
  }
}

function* applyRouteBoundaryOverlays(viewState: MapViewState) {
  if (viewState.boundaryKey) {
    // Split the boundariesViewSettingsChanged dispatch into two — non-persisted first,
    // persisted last — see Map saga's onInitializeMapView for the full explanation.
    const persistedType: BoundaryType = yield select(getBoundaryType);
    const { boundaryKey } = viewState;
    const otherType =
      persistedType === BoundaryType.TERRITORY ? BoundaryType.GEOGRAPHY : BoundaryType.TERRITORY;
    if (boundaryKey[otherType]) {
      yield put(
        boundariesViewSettingsChanged({ boundaryKey: { [otherType]: boundaryKey[otherType] } })
      );
    }
    if (boundaryKey[persistedType]) {
      yield put(
        boundariesViewSettingsChanged({
          boundaryKey: { [persistedType]: boundaryKey[persistedType] },
        })
      );
    }
  }
}

export function* onInitializeRouteView({
  payload,
}: ReturnType<typeof initializeRouteView.request>) {
  const { callback, routeId } = payload;
  try {
    yield call(initRecordListState);
    const { route, routePreview }: { route: Route; routePreview?: RoutePreview } = yield call(
      fetchRouteAndPreview,
      routeId
    );
    yield put(initializeRouteView.success({ route, routePreview }));
    const viewState: MapViewState = yield call(buildInitialMapViewState, route);
    // applyRouteViewSettings writes only the display subset of MapViewState; the interaction
    // fields (lasso/search/tool/sidebarVisible) were reset to defaults by clearRouteDetails
    // (which always precedes init) and equal the seed's values — so merging the display subset
    // == the old full-replace seed. Do NOT change the merge handler without revisiting this.
    yield put(applyRouteViewSettings(viewState));
    yield call(applyRouteBoundaryOverlays, viewState);
    if (callback) {
      const bounds: google.maps.LatLngBounds = yield call(computeRouteBounds, route, routePreview);
      yield call(callback, bounds);
    }
  } catch (error) {
    yield put(initializeRouteView.failure());
    yield put(handleError({ error }));
  }
}

export function* onInitializeRouteCreate({
  payload,
}: ReturnType<typeof initializeRouteCreate.request>) {
  const { callback, date } = payload;
  try {
    yield call(initRecordListState);
    // the redux selection outlives the page it was made on, and the applicator re-applies it on
    // mount, so a brand-new route has to clear it as well as the persisted settings
    yield put(
      selectSavedFilter({ entityType: EntityType.PIN, savedFilter: undefined, skipUrlUpdate: true })
    );
    const viewState: MapViewState = yield call(buildInitialMapViewState, undefined, {
      includeRouteStopsTypes: false,
      // a brand-new route starts with no filter rather than inheriting the last one
      inheritSavedFilter: false,
    });
    // See onInitializeRouteView for why merging the display subset == the old full-replace seed.
    // Create always opens with "Show other pins" ON — a new route has no stops yet, so the
    // surrounding pins are the useful starting context. Not persisted (see onFetchPins).
    yield put(applyRouteViewSettings({ ...viewState, showOther: true }));
    yield call(applyRouteBoundaryOverlays, viewState);
    if (callback) {
      try {
        const position: GeolocationPosition = yield call(geoService.getCurrentPosition);
        yield call(callback, position.coords);
      } catch {
        yield call(callback);
      }
    }

    const owner: User = yield select(getCurrentUser);
    yield put(initializeRouteCreate.success({ date, owner }));
    yield put(fetchScheduledActivities.request());
  } catch (error) {
    yield put(initializeRouteCreate.failure());
    yield put(handleError({ error }));
  }
}

export function* onInitializeRouteEdit({
  payload,
}: ReturnType<typeof initializeRouteEdit.request>) {
  const { callback, routeId } = payload;
  try {
    yield call(initRecordListState);
    const { route, routePreview }: { route: Route; routePreview?: RoutePreview } = yield call(
      fetchRouteAndPreview,
      routeId
    );
    if (!route.accessStatus.update) {
      yield put(push(`${Path.ROUTE}/${RouteSubPath.VIEW}/${route.id}`));
      yield put(initializeRouteEdit.failure());
      return;
    }
    yield put(initializeRouteEdit.success({ route, routePreview }));
    const viewState: MapViewState = yield call(buildInitialMapViewState, route);
    // See onInitializeRouteView for why merging the display subset == the old full-replace seed.
    yield put(applyRouteViewSettings(viewState));
    yield call(applyRouteBoundaryOverlays, viewState);
    if (callback) {
      const bounds: google.maps.LatLngBounds = yield call(computeRouteBounds, route, routePreview);
      yield call(callback, bounds);
    }
  } catch (error) {
    yield put(initializeRouteEdit.failure());
    yield put(handleError({ error }));
  }
}

export function* routeDetailsSaga() {
  yield all([
    takeLatest(initializeRouteCreate.request, onInitializeRouteCreate),
    takeLatest(initializeRouteView.request, onInitializeRouteView),
    takeLatest(initializeRouteEdit.request, onInitializeRouteEdit),
    fork(editorSaga),
    fork(viewSaga),
    fork(mapSaga),
    fork(routeLeadFinderSagas),
  ]);
}
